// The secrets an e-mailed link (or a provider's return) carries in the page address — an invitation
// token, a password-reset token, a one-time sign-in code — are taken OUT of the address before
// anything else runs: analytics record the address of every page, and the browser keeps it in its
// history. They wait in sessionStorage (this tab only, gone when it closes), where the screens
// read them.
const KEY = 'dosen.auth.link';

const SECRET_PARAMS = new Map<string, string[]>([
  ['/accept-invitation', ['token', 'ticket']],
  ['/reset-password', ['token']],
  ['/sso', ['code']]
]);

/** Per screen (a provider sign-in started from an invitation needs the invitation's AND its own). */
type StoredLinks = Record<string, Record<string, string>>;

function readStored(): StoredLinks {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function store(route: string, values: Record<string, string>): boolean {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...readStored(), [route]: values }));
    return true;
  } catch {
    return false;
  }
}

/**
 * Call before the app mounts (and again when the address changes under an open app). Returns true
 * when a secret was taken out of the address. When it cannot be kept elsewhere (no
 * sessionStorage) the address is left as it is and the screens read it from there.
 */
export function captureLinkSecrets(): boolean {
  const { hash } = window.location;
  const cut = hash.indexOf('?');
  const route = (cut < 0 ? hash : hash.slice(0, cut)).slice(1);
  const names = SECRET_PARAMS.get(route);
  if (!names || cut < 0) return false;
  const params = new URLSearchParams(hash.slice(cut + 1));
  const values: Record<string, string> = {};
  names.forEach((name) => {
    const value = params.get(name);
    if (value) values[name] = value;
  });
  if (Object.keys(values).length === 0 || !store(route, values)) return false;
  names.forEach((name) => params.delete(name));
  const rest = params.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${window.location.search}#${route}${rest ? `?${rest}` : ''}`
  );
  return true;
}

/**
 * An invitation issued through Auth0 earlier arrives as `…?invitation=<ticket>&organization=…` on
 * the page itself. With transparent authentication it is accepted on the app's own screen: the
 * ticket is kept like the other link secrets and the browser moves to that screen — before
 * anything mounts. Returns true when the page is on its way there.
 */
export function redirectTicketLanding(): boolean {
  const ticket = new URLSearchParams(window.location.search).get('invitation');
  if (!ticket) return false;
  const base = `${window.location.origin}${window.__APP_BASE__ || ''}/#/accept-invitation`;
  const kept = store('/accept-invitation', { ticket });
  window.location.replace(kept ? base : `${base}?ticket=${encodeURIComponent(ticket)}`);
  return true;
}

/** A secret of the link this tab was opened with ('' when there is none for that screen). */
export function linkSecret(route: string, name: string): string {
  // (looked up as entries: a screen's name is never taken for a property of the object itself)
  const values = new Map(Object.entries(readStored())).get(route);
  const kept = values ? new Map(Object.entries(values)).get(name) : undefined;
  if (typeof kept === 'string') return kept;
  // Not captured (storage unavailable): still in the address.
  const { hash } = window.location;
  const cut = hash.indexOf('?');
  if (cut < 0 || hash.slice(1, cut) !== route) return '';
  return new URLSearchParams(hash.slice(cut + 1)).get(name) || '';
}

/** The link has done its job (or is of no use any more). */
export function forgetLinkSecrets(route: string): void {
  try {
    const { [route]: done, ...rest } = readStored();
    if (done === undefined) return;
    if (Object.keys(rest).length) sessionStorage.setItem(KEY, JSON.stringify(rest));
    else sessionStorage.removeItem(KEY);
  } catch {
    /* storage unavailable */
  }
}
